import React, { memo, useState } from "react";
import { Card, CardActionArea, Box, Typography, useTheme, CardProps, Skeleton, Tooltip, CircularProgress } from "@mui/material";
import { FlexibleDataItem, ApiEndpoint } from "../../../types";
import { getSafeThumbnailUrl } from "../../../utilities/mediaUtils";
import { getDataItemName } from "../../../utilities/dataSourceAdapters";
import { FulcrumImage } from "../FulcrumImage";
import { TileStatusOverlay } from "../TileStatusOverlay";
import { Description, Image, LayersOutlined, PictureAsPdf, VideoFile } from "@mui/icons-material";
import getMediaType from "../../../utilities/getMediaType";
import { SPATIAL_STATUS_TILE_LABEL } from "../../../constants/spatialDataset";
import { SpatialDatasetProcessingStatus } from "../../../types";
import SpatialDatasetVisibilityCheckbox from "../../spatial-datasets/SpatialDatasetVisibilityCheckbox";

interface DataItemCardProps {
  item: FlexibleDataItem;
  compact?: boolean;
  imageUrl?: string;
  onSelect: (item: FlexibleDataItem) => void;
  preview?: boolean;
  endpoint?: ApiEndpoint | null;
  borderColor?: string; // For endpoint-based coloring in related items mode
  isPending?: boolean; // Use this to show some action is pending
  isActive?: boolean;
}

function FileType({ fileType }: { fileType: string }) {
  if (fileType === "image" || fileType?.startsWith("image")) {
    return <Image />;
  }
  if (fileType === "video" || fileType?.startsWith("video")) {
    return <VideoFile />;
  }
  if (fileType === "application/pdg" || fileType === "pdf") {
    return <PictureAsPdf />;
  }
  return <Description />;
}

const DataItemCard = memo(
  ({
    item,
    compact,
    imageUrl,
    onSelect,
    preview = false,
    endpoint,
    sx,
    borderColor,
    isPending = false,
    isActive = false,
    ...restProps
  }: DataItemCardProps & Partial<Omit<CardProps, "onSelect">>) => {
    const [imageError, setImageError] = useState(false);
    const [isImgLoading, setIsImgLoading] = useState(true);
    const [useFullImageFallback, setUseFullImageFallback] = useState(false);
    const mediaType = getMediaType(item);
    const theme = useTheme();

    // Determine if we should show the fallback
    // Prioritize the imageUrl prop (from DataGrid), fall back to getSafeThumbnailUrl
    const safeImageUrl = imageUrl || getSafeThumbnailUrl(item);
    // Newly-uploaded images may not have a generated thumbnail.jpg on the CDN yet,
    // so fall back to the full-resolution image before giving up on showing a preview.
    const fullImageUrl = (item as any)?.primary_image?.url || (item as any)?.url;
    const displayImageUrl = useFullImageFallback && fullImageUrl ? fullImageUrl : safeImageUrl;
    const showFallback = !displayImageUrl || imageError;

    // Spatial dataset status overlay (§6c)
    const isSpatial = endpoint?.entityType === "spatial-dataset";
    const isTabular = isSpatial && (item as any).dataset_type === "tabular";
    const spatialStatus = isSpatial ? (((item as any).processing_status as SpatialDatasetProcessingStatus) ?? "pending") : null;
    // Tabular datasets never produce a map display_url, so a completed tabular
    // dataset is "done" even without one — don't keep it in the loading state.
    const spatialNotViewable = isSpatial && (spatialStatus !== "completed" || (!isTabular && !(item as any).display_url));

    // Handle image loading states for better UX in compact mode
    const handleImageError = () => {
      if (!useFullImageFallback && fullImageUrl && fullImageUrl !== safeImageUrl) {
        setUseFullImageFallback(true);
        setIsImgLoading(true);
        return;
      }
      setImageError(true);
      setIsImgLoading(false);
    };

    const handleImageLoad = () => {
      setIsImgLoading(false);
    };

    // Reset states when image URL changes
    React.useEffect(() => {
      if (safeImageUrl) {
        setIsImgLoading(true);
        setImageError(false);
        setUseFullImageFallback(false);
      } else {
        setIsImgLoading(false);
      }
    }, [safeImageUrl]);

    const cardName = getDataItemName(item, endpoint || undefined);
    return (
      <Box sx={{ position: "relative", height: "100%" }}>
        <CardActionArea
          sx={{
            position: "relative",
            display: "block",
            cursor: preview ? "default" : "pointer",
            borderRadius: "8px", // Slightly more rounded like MediaDrawer
            overflow: "hidden",
            height: "100%",
            "&:focus-visible": {
              outline: "1px solid",
              outlineColor: "grey.300",
              outlineOffset: "2px!important"
            }
          }}
          onClick={() => {
            if (!preview) onSelect(item);
          }}
          disabled={isPending}
        >
          <Card
            {...restProps}
            sx={{
              position: "relative",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: isActive ? "primary.main" : "#344054", // Slightly more opaque than before
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)", // Enhanced shadow
              borderRadius: 2, // Modern rounded corners
              overflow: "hidden",
              transition: "all 0.3s ease-out", // Smooth transitions
              border: `0.0625rem solid ${borderColor || "#667085"}`,
              aspectRatio: "none",
              "&:hover": {
                background: theme.palette?.primary?.main,
                color: theme.palette?.secondary?.main,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)"
              },
              ...sx
            }}
          >
            <Box sx={{ position: "relative", height: compact ? "10rem" : "7.75rem", width: "100%", zIndex: 2, overflow: "hidden" }}>
              {/* Spatial dataset status overlay — shown while not yet viewable */}
              {isSpatial && spatialNotViewable && spatialStatus && (
                <TileStatusOverlay
                  variant={spatialStatus === "failed" ? "error" : "loading"}
                  label={SPATIAL_STATUS_TILE_LABEL[spatialStatus]}
                  subLabel={spatialStatus === "failed" ? ((item as any).processing_error as string | undefined)?.slice(0, 60) : undefined}
                />
              )}
              {/* Completed spatial datasets with no thumbnail yet — generic icon fallback */}
              {isSpatial && !spatialNotViewable && !safeImageUrl && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    background: "#626c82",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    "& > *": { fontSize: compact ? 28 : 46, color: "#dcdcdc" }
                  }}
                >
                  <LayersOutlined />
                </Box>
              )}
              {/* Image loading skeleton*/}
              {isImgLoading && (
                <>
                  <Skeleton
                    sx={{ position: "absolute", height: "100%", width: "100%", top: 0, left: 0, zIndex: 2 }}
                    variant='rectangular'
                  ></Skeleton>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 3
                    }}
                  >
                    <CircularProgress size={compact ? 20 : 28} sx={{ color: "#98A2B3" }} />
                  </Box>
                </>
              )}

              {/* Card thumbnail */}
              <FulcrumImage
                src={displayImageUrl} // Use safeImageUrl, falling back to full-res image if the thumbnail 404s
                alt={getDataItemName(item, endpoint || undefined)}
                region='AU'
                thumbnail={false} // Full size image, not thumbnail
                sx={{
                  position: "relative",
                  width: "100%",
                  height: compact ? 90 : "100%",
                  objectFit: "cover",
                  zIndex: 1
                }}
                onError={handleImageError}
                onLoad={handleImageLoad}
              />
              {showFallback && (
                <Box
                  sx={{
                    position: "absolute",
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    background: "#626c82",
                    alignItems: "center",
                    justifyContent: "center",
                    inset: 0,
                    zIndex: 2,

                    "& > *": {
                      fontSize: compact ? 28 : 46,
                      color: "#dcdcdc"
                    }
                  }}
                >
                  <FileType fileType={mediaType} />
                </Box>
              )}
            </Box>

            {/* card title */}
            <Box sx={{ p: "0.5rem", width: "100%", boxSizing: "border-box" }}>
              <Tooltip title={cardName}>
                <Typography
                  variant='h6'
                  sx={{
                    color: "#fff",
                    fontSize: "0.9rem",
                    fontWeight: 500, // Match MediaDrawer font weight
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textTransform: "capitalize",
                    overflow: "hidden"
                  }}
                >
                  {cardName}
                </Typography>
              </Tooltip>
            </Box>
          </Card>
        </CardActionArea>
        {isSpatial && !isTabular && spatialStatus === "completed" && (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              zIndex: 4
            }}
          >
            <SpatialDatasetVisibilityCheckbox item={item} />
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for memo
    return (
      prevProps.imageUrl === nextProps.imageUrl &&
      prevProps.item === nextProps.item && // CHECK THE ITEM REFERENCE (was missing!)
      prevProps.preview === nextProps.preview &&
      prevProps.isActive === nextProps.isActive
    );
  }
);

export default DataItemCard;
